
import React, { useState } from "react";
import { useAuth } from "@/components/auth/AuthProvider";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ClaimButton } from "./ClaimButton";
import { ClaimDialog } from "./ClaimDialog";
import { ClaimingSuccessNotice } from "./ClaimingSuccessNotice";

interface ClaimingSectionProps {
  nhSlug: string;
  centerName: string;
  isClaimed: boolean;
}

export function ClaimingSection({ nhSlug, centerName, isClaimed }: ClaimingSectionProps) {
  const { user } = useAuth();
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [claimSubmitted, setClaimSubmitted] = useState(false);
  const [isClaimLoading, setIsClaimLoading] = useState(false);

  const [formData, setFormData] = useState({
    role: "",
    email: "",
    contactPhone: "",
    verificationNote: "",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value: string) => {
    setFormData({ ...formData, role: value });
  };

  const handleDialogOpenChange = (open: boolean) => {
    setClaimDialogOpen(open);
    if (!open) {
      setFormData({
        role: "",
        email: "",
        contactPhone: "",
        verificationNote: "",
      });
    }
  };

  const isEmailVerified = () => {
    if (!formData.email || !centerName) return false;
    const emailDomain = formData.email.split('@')[1]?.toLowerCase();
    const centreNameWords = centerName.toLowerCase().split(' ');
    return centreNameWords.some(word => 
      emailDomain?.includes(word.replace(/[^a-z0-9]/g, ''))
    );
  };

  const handleClaimSubmit = async () => {
    if (!user) {
      toast.info("You need to sign in first.");
      return;
    }
    setIsClaimLoading(true);
    try {
      const { error } = await supabase.from("nursing_home_claims").insert({
        nursing_home_id: nhSlug,
        user_id: user.id,
        status: isEmailVerified() ? "pending_review" : "pending_call",
        verification_documents: {
          role: formData.role,
          email: formData.email,
          contactPhone: formData.contactPhone,
          note: formData.verificationNote,
          emailVerified: isEmailVerified(),
        },
        notes: formData.verificationNote,
      });

      if (error) throw error;

      setClaimDialogOpen(false);
      setClaimSubmitted(true);
      
      if (isEmailVerified()) {
        toast.success("Your claim request has been submitted and will be reviewed shortly.");
      } else {
        toast.success("Thanks! Our team will contact you to verify your association with the facility.");
      }
    } catch (err) {
      toast.error("Failed to submit claim request.");
      console.error(err);
    } finally {
      setIsClaimLoading(false);
    }
  };

  if (isClaimed) {
    return <ClaimingSuccessNotice centerName={centerName} />;
  }

  if (claimSubmitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-green-800">
        <div className="font-semibold mb-1">Thanks for submitting your claim!</div>
        <div className="text-sm">
          Our team will review your request and reach out to you soon regarding the verification.
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <ClaimButton onClick={() => setClaimDialogOpen(true)} />
      <ClaimDialog
        centerName={centerName}
        open={claimDialogOpen}
        onOpenChange={handleDialogOpenChange}
        isSubmitting={isClaimLoading}
        formData={formData}
        onInputChange={handleInputChange}
        onSelectChange={handleSelectChange}
        onSubmit={handleClaimSubmit}
      />
    </div>
  );
}
